import { Alert } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../api/http';
import InvoiceApi from '../../api/InvoiceApi';
import { useInvoicesForPolicyholder } from '../../api/PolicyholderApi';
import { useAuthContext } from '../../context/auth';
import {
	FETCH_INVOICES_FAILURE,
	PAY_INVOICE_FAILURE,
	PAY_INVOICE_SUCCESS,
} from '../../data/labelsAndMessages';
import { PaymentCreateRequest } from '../../types/policy';
import NewInvoicesTable from '../NewInvoicesTable';
import { errorToast, successToast } from '../Toast';

const InvoicesView = () => {
	const authData = useAuthContext();
	const params = useParams();
	const queryClient = useQueryClient();
	const { policyholderId } = params;
	const authToken = authData?.auth?.authorizationToken;
	const hasPermission = authData?.hasPermission;
	const {
		data: invoicesResponse,
		isLoading: areInvoicesLoading,
		isError,
	} = useInvoicesForPolicyholder(
		authToken,
		policyholderId,
		hasPermission('invoice.fetch'),
	);
	const invoices = invoicesResponse?.data || [];

	const payInvoice = async (locator: string, req: PaymentCreateRequest) => {
		try {
			await InvoiceApi.payInvoice(
				authData.auth.authorizationToken,
				locator,
				req,
			);
			successToast(PAY_INVOICE_SUCCESS());
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast(PAY_INVOICE_FAILURE());
		}

		try {
			queryClient.invalidateQueries(['invoices', policyholderId, authToken]);
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		}
	};

	if (isError) {
		return (
			<Alert security='error' severity='error'>
				{FETCH_INVOICES_FAILURE()}
			</Alert>
		);
	}

	return (
		<NewInvoicesTable
			policyholderLocator={policyholderId}
			isLoading={areInvoicesLoading}
			invoices={invoices}
			payInvoice={payInvoice}
			// Note: use currency from InvoiceResponse instead for invoices of policyholder
			useInvoiceCurrency
		/>
	);
};

export default InvoicesView;
